<template>
  <div class="cs-root sc-page-root">
    <FiPageRail title="Coaching Dashboard" class="cs-rail">
      <template #actions>
        <coaching-portal-help />
        <b-button v-if="canEditSettings" size="sm" variant="outline-secondary"
                  title="Coaching SLA settings" @click="openSettings">
          <i class="fa fa-cog"></i>&nbsp;Settings
        </b-button>
      </template>
    </FiPageRail>

    <div v-if="actingTenantDiffers" class="cs-tenant-note">
      <i class="fa fa-building-o" aria-hidden="true"></i>
      Viewing <b>{{ actingTenantLabel }}</b> — not your selected fleet
    </div>

    <div v-if="loading && !tickets.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="loadAll()">Retry</b-button>
    </div>

    <div v-else :class="{ 'cs-stale': loading }">
      <!-- headline KPIs: the effectiveness triad + queue health -->
      <div class="cs-metrics-row">
        <div class="cs-tile">
          <h5>Coaching effectiveness</h5>
          <h2 :class="effectivenessClass">{{ effectiveness ? fmtPct(effectiveness.recidivism.effectiveness_pct) : (effectivenessLoading ? '…' : '—') }}</h2>
          <small v-if="effectiveness" class="text-muted">no repeat within {{ effectiveness.recidivism.window_days }}d · industry ~{{ effectiveness.benchmarks.industry_effectiveness_baseline_pct }}%</small>
        </div>
        <div class="cs-tile">
          <h5>Median time to coach</h5>
          <h2>{{ effectiveness && effectiveness.timeliness.median_days != null ? effectiveness.timeliness.median_days + 'd' : (effectivenessLoading ? '…' : '—') }}</h2>
          <small class="text-muted">{{ effectiveness ? fmtPct(effectiveness.timeliness.pct_within_7_days) : '—' }} within 7d · {{ effectiveness ? fmtPct(effectiveness.timeliness.pct_within_2_days) : '—' }} within 48h</small>
        </div>
        <div class="cs-tile">
          <h5>Coverage</h5>
          <h2>{{ effectiveness ? fmtPct(effectiveness.coverage.pct_closed) : (effectivenessLoading ? '…' : '—') }}</h2>
          <small v-if="effectiveness" class="text-muted">{{ effectiveness.coverage.closed }} of {{ effectiveness.coverage.total }} signed off</small>
        </div>
        <div class="cs-tile">
          <h5>Open now</h5>
          <h2 :class="summary.escalated ? 'text-danger' : ''">{{ (summary.open || 0) + (summary.scheduled || 0) }}</h2>
          <small :class="summary.escalated ? 'text-danger font-weight-bold' : 'text-muted'">
            {{ summary.past_due || 0 }} past due · {{ summary.escalated || 0 }} escalated
          </small>
        </div>
        <!-- entirely scorecard-derived: with no scorecard flag there are no
             rows to count and nowhere to click through to, so the tile goes
             rather than reading a permanent zero -->
        <router-link
          v-if="hasScorecardFlags"
          :to="{ name: 'coaching_flagged', query: { tenant: tenant } }"
          class="cs-tile cs-tile--link"
        >
          <h5>Flagged drivers</h5>
          <h2>{{ flaggedError ? '—' : flaggedDrivers.length }}</h2>
          <small class="text-muted">{{ flaggedError ? 'scorecard unavailable' : scorecardPeriodLabel }} →</small>
        </router-link>
        <!-- these counts come from recognition TICKETS, not scorecards, so the
             tile stays meaningful either way -- only the click-through to the
             (scorecard-fed) candidates page drops away -->
        <component
          :is="hasScorecardFlags ? 'router-link' : 'div'"
          :to="hasScorecardFlags ? { name: 'coaching_recognition', query: { tenant: tenant } } : null"
          class="cs-tile"
          :class="{ 'cs-tile--link': hasScorecardFlags }"
        >
          <h5>Recognition</h5>
          <h2 class="cs-positive">{{ recognitionSummary.completed }}</h2>
          <small class="text-muted">
            delivered · {{ recognitionSummary.open + recognitionSummary.scheduled }} waiting{{ hasScorecardFlags ? ' →' : '' }}
          </small>
        </component>
      </div>

      <!-- the hero: raised-tickets timeline colored by status (the ERM
           Analytics "Events over time" pattern), full-width above the grid -->
      <coaching-status-timeline class="cs-timeline-hero" :tickets="coachingTickets" />

      <div class="cs-grid">
        <!-- moved from the portal -->
        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Most common coaching topics</div>
          <div class="cs-panel-body">
            <div v-if="!topicCounts.length" class="cs-small text-muted">Nothing on file yet.</div>
            <div v-for="tc in topicCounts" :key="tc.topic" class="cs-bar-row">
              <span class="cs-bar-label" :title="tc.topic">{{ tc.topic }}</span>
              <span class="cs-bar-track"><span class="cs-bar-fill" :style="{ width: tc.pct + '%' }"></span></span>
              <span class="cs-bar-count">{{ tc.count }}</span>
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Queue status <span class="cs-hint">coaching tickets, whole history</span></div>
          <div class="cs-panel-body">
            <div v-for="s in statusBreakdown" :key="s.key" class="cs-bar-row">
              <span class="cs-bar-label">{{ s.label }}</span>
              <span class="cs-bar-track"><span class="cs-bar-fill" :class="s.cls" :style="{ width: s.pct + '%' }"></span></span>
              <span class="cs-bar-count">{{ s.count }}</span>
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Where tickets come from</div>
          <div class="cs-panel-body">
            <div v-if="!sourceCounts.length" class="cs-small text-muted">Nothing on file yet.</div>
            <div v-for="s in sourceCounts" :key="s.key" class="cs-bar-row">
              <span class="cs-bar-label">{{ s.label }}</span>
              <span class="cs-bar-track"><span class="cs-bar-fill" :style="{ width: s.pct + '%' }"></span></span>
              <span class="cs-bar-count">{{ s.count }}</span>
            </div>
            <div class="cs-small text-muted mt-2">
              Scorecard flags lead (nothing has gone wrong yet); ERM events lag (something already did).
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Sign-off outcomes <span class="cs-hint">what coaching conversations concluded</span></div>
          <div class="cs-panel-body">
            <div v-if="!outcomeCounts.length" class="cs-small text-muted">No sign-offs yet.</div>
            <div v-for="o in outcomeCounts" :key="o.key" class="cs-bar-row">
              <span class="cs-bar-label" :title="o.label">{{ o.label }}</span>
              <span class="cs-bar-track"><span class="cs-bar-fill" :style="{ width: o.pct + '%' }"></span></span>
              <span class="cs-bar-count">{{ o.count }}</span>
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Six-month flow <span class="cs-hint">raised vs signed off, per month</span></div>
          <div class="cs-panel-body">
            <div v-for="m in monthlyFlow" :key="m.month" class="cs-month-row">
              <span class="cs-bar-label">{{ m.label }}</span>
              <span class="cs-month-bars">
                <span class="cs-bar-track"><span class="cs-bar-fill" :style="{ width: m.createdPct + '%' }"></span></span>
                <span class="cs-bar-track"><span class="cs-bar-fill cs-bar-fill--good" :style="{ width: m.closedPct + '%' }"></span></span>
              </span>
              <span class="cs-bar-count">{{ m.created }} / {{ m.closed }}</span>
            </div>
            <div class="cs-small text-muted mt-2">
              <span class="cs-swatch"></span> raised &nbsp; <span class="cs-swatch cs-swatch--good"></span> signed off —
              a widening gap means the queue is falling behind.
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel" no-body>
          <div class="cs-panel-head">Most coached drivers</div>
          <div class="cs-panel-body">
            <div v-if="!topDrivers.length" class="cs-small text-muted">Nothing on file yet.</div>
            <div v-for="d in topDrivers" :key="d.name" class="cs-flag-row">
              <div class="cs-driver-line">
                <b>{{ d.name }}</b>
                <span class="cs-small text-muted"> · {{ d.total }} ticket{{ d.total === 1 ? '' : 's' }} · {{ d.topics }}</span>
              </div>
              <b-badge :variant="d.open ? 'danger' : 'success'">{{ d.open ? d.open + ' open' : 'clear' }}</b-badge>
            </div>
          </div>
        </b-card>

        <b-card class="cs-panel cs-panel--wide" no-body>
          <div class="cs-panel-head">By coach <span class="cs-hint">workload and closing speed — visibility, not a leaderboard</span></div>
          <div class="cs-panel-body">
            <div v-if="!effectiveness || !effectiveness.by_coach.length" class="cs-small text-muted">No tickets yet.</div>
            <div v-else class="cs-table-wrap">
              <b-table :items="effectiveness.by_coach" :fields="coachFields" small class="mb-0">
                <template slot="assigned_to_name" slot-scope="data">
                  <b>{{ data.item.assigned_to_name }}</b>
                </template>
                <template slot="escalated" slot-scope="data">
                  <span :class="data.item.escalated ? 'text-danger font-weight-bold' : 'text-muted'">{{ data.item.escalated }}</span>
                </template>
                <template slot="median_days_to_close" slot-scope="data">
                  {{ data.item.median_days_to_close == null ? '—' : data.item.median_days_to_close + 'd' }}
                </template>
              </b-table>
            </div>
          </div>
        </b-card>
      </div>

      <p class="cs-small text-muted mt-3">
        Effectiveness counts a sign-off as a repeat when the same driver is re-ticketed on the same
        topic within the window; sign-offs younger than the window aren't counted either way.
        Recognition records are excluded from every coaching figure on this page.
      </p>
    </div>

    <!-- R3: per-tenant SLA settings (fleet admins only) — the dashboard owns
         the dials because this is the page that measures them -->
    <b-modal id="cs-settings-modal" hide-footer title="Coaching SLA settings">
      <div class="cs-label">Due date default</div>
      <b-input-group append="days after a ticket is raised" class="mb-3">
        <b-form-input v-model.number="settingsForm.due_days_default" type="number" min="1" max="90" class="cs-num-input" />
      </b-input-group>
      <div class="cs-label">Escalate when overdue by</div>
      <b-input-group append="days past due" class="mb-3">
        <b-form-input v-model.number="settingsForm.escalate_after_overdue_days" type="number" min="1" max="60" class="cs-num-input" />
      </b-input-group>
      <div class="cs-label">Repeat window</div>
      <b-input-group append="days after sign-off" class="mb-1">
        <b-form-input v-model.number="settingsForm.recidivism_window_days" type="number" min="14" max="180" class="cs-num-input" />
      </b-input-group>
      <p class="cs-small text-muted mb-3">
        How long after a sign-off we watch for the same behavior to come back.
        If it doesn't, the coaching counted as effective. The industry standard
        is 60 days.
      </p>
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide('cs-settings-modal')">Cancel</b-button>
        <b-button variant="primary" :disabled="settingsSaving" @click="saveSettings">
          {{ settingsSaving ? 'Saving…' : 'Save settings' }}
        </b-button>
      </div>
    </b-modal>

  </div>
</template>

<script>
import moment from 'moment'
import { get_coaching_effectiveness, update_coaching_settings } from '../networking/apis'
import coachingScopeMixin, { OPEN_STATUSES } from '../mixins/coaching_scope_mixin'
import CoachingPortalHelp from './coaching_portal_help.vue'
import CoachingStatusTimeline from './coaching_status_timeline.vue'

const STATUS_ROWS = [
  { key: 'open', label: 'Open', cls: 'cs-bar-fill--risk' },
  { key: 'scheduled', label: 'Scheduled', cls: 'cs-bar-fill--warn' },
  { key: 'completed', label: 'Completed', cls: 'cs-bar-fill--good' },
]
const SOURCE_LABELS = { scorecard_kpi: 'Scorecard KPI', erm_event: 'ERM event', accident: 'Accident', manual: 'Manual' }
const OUTCOME_LABELS = {
  delivered: 'Delivered',
  retraining_scheduled: 'Retraining scheduled',
  written_warning: 'Written warning issued',
  dismissed_not_at_fault: 'Dismissed — not at fault',
}

export default {
  // the Coaching Portal dashboard: KPIs and breakdowns only. the flagged
  // and recognition tables live on their own pages; the shared data
  // plumbing lives in coaching_scope_mixin.
  name: 'coaching_stats',
  components: { CoachingPortalHelp, CoachingStatusTimeline },
  mixins: [coachingScopeMixin],
  data() {
    return {
      effectiveness: null,
      effectivenessLoading: false,

      settingsForm: { due_days_default: 14, escalate_after_overdue_days: 7, recidivism_window_days: 60 },
      settingsSaving: false,
    }
  },
  computed: {
    canEditSettings() {
      // FI-1647: UI gating uses the effective role so a muggle-view admin
      // sees the customer experience; server-side PATCH is erm-admin gated
      const viewAs = this.$store.state.profile.view_as
      const role = this.user.role === 'admin' && viewAs === 'non-admin' ? 'customer' : this.user.role
      return ['admin', 'customer-admin'].includes(role)
    },
    topicCounts() {
      const counts = {}
      this.coachingTickets.forEach(t => { counts[t.topic] = (counts[t.topic] || 0) + 1 })
      const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8)
      const max = sorted.length ? sorted[0][1] : 1
      return sorted.map(([topic, count]) => ({ topic, count, pct: Math.round((count / max) * 100) }))
    },
    statusBreakdown() {
      const max = Math.max(1, ...STATUS_ROWS.map(s => this.summary[s.key] || 0))
      return STATUS_ROWS.map(s => ({
        ...s, count: this.summary[s.key] || 0,
        pct: Math.round(((this.summary[s.key] || 0) / max) * 100),
      }))
    },
    sourceCounts() {
      const by = (this.summary && this.summary.by_source) || {}
      const entries = Object.entries(by).sort((a, b) => b[1] - a[1])
      const max = entries.length ? entries[0][1] : 1
      return entries.map(([key, count]) => ({
        key, count, label: SOURCE_LABELS[key] || key,
        pct: Math.round((count / max) * 100),
      }))
    },
    outcomeCounts() {
      const counts = {}
      this.coachingTickets.forEach(t => {
        const outcome = t.sign_off && t.sign_off.outcome
        if (outcome) counts[outcome] = (counts[outcome] || 0) + 1
      })
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1])
      const max = entries.length ? entries[0][1] : 1
      return entries.map(([key, count]) => ({
        key, count, label: OUTCOME_LABELS[key] || key,
        pct: Math.round((count / max) * 100),
      }))
    },
    monthlyFlow() {
      const months = []
      for (let back = 5; back >= 0; back--) {
        // UTC on both sides: created_at/completed_at are UTC ISO stamps,
        // so the bucket labels must be UTC months or boundary-evening
        // tickets fall between buckets
        const m = moment.utc().subtract(back, 'months')
        months.push({ month: m.format('YYYY-MM'), label: m.format('MMM') })
      }
      const rows = months.map(m => ({
        ...m,
        created: this.coachingTickets.filter(t => (t.created_at || '').slice(0, 7) === m.month).length,
        closed: this.coachingTickets.filter(t => (t.completed_at || '').slice(0, 7) === m.month).length,
      }))
      const max = Math.max(1, ...rows.map(r => Math.max(r.created, r.closed)))
      return rows.map(r => ({
        ...r,
        createdPct: Math.round((r.created / max) * 100),
        closedPct: Math.round((r.closed / max) * 100),
      }))
    },
    topDrivers() {
      const by = {}
      this.coachingTickets.forEach(t => {
        const row = by[t.driver_name] = by[t.driver_name] || { name: t.driver_name, total: 0, open: 0, topicSet: {} }
        row.total += 1
        if (OPEN_STATUSES.includes(t.status)) row.open += 1
        row.topicSet[t.topic] = true
      })
      return Object.values(by)
        .sort((a, b) => b.total - a.total || b.open - a.open)
        .slice(0, 8)
        .map(r => ({ ...r, topics: Object.keys(r.topicSet).slice(0, 3).join(' · ') }))
    },
    coachFields() {
      return [
        { key: 'assigned_to_name', label: 'Coach' },
        { key: 'open', label: 'Open' },
        { key: 'past_due', label: 'Past due' },
        { key: 'escalated', label: 'Escalated' },
        { key: 'closed', label: 'Signed off' },
        { key: 'median_days_to_close', label: 'Median days to close' },
      ]
    },
    effectivenessClass() {
      const pct = this.effectiveness && this.effectiveness.recidivism.effectiveness_pct
      if (pct == null) return 'text-muted'
      return pct >= this.effectiveness.benchmarks.industry_effectiveness_baseline_pct
        ? 'text-success' : 'text-warning'
    },
  },
  created() {
    // the mixin's created() loads tickets + scorecards; effectiveness is
    // dashboard-only
    this.loadEffectiveness()
  },
  methods: {
    fmtPct(value) {
      return value == null ? '—' : `${value}%`
    },
    // retry button target: everything this page shows
    loadAll() {
      this.loadTickets()
      this.loadFlaggedDrivers()
      this.loadEffectiveness()
    },
    async loadEffectiveness() {
      this.effectivenessLoading = true
      try {
        const resp = await get_coaching_effectiveness(this.tenant)
        this.effectiveness = (resp.value && resp.value.data) || null
      } catch (e) {
        // the KPI tiles degrade to em-dashes; the rest of the page stands
        this.effectiveness = null
      } finally {
        this.effectivenessLoading = false
      }
    },
    // mixin hook: effectiveness and the settings dialog are tenant-scoped too
    onTenantScopeReset() {
      this.$bvModal.hide('cs-settings-modal')
      this.effectiveness = null
      this.loadEffectiveness()
    },

    openSettings() {
      this.settingsForm = { ...(this.settings || this.settingsForm) }
      this.$bvModal.show('cs-settings-modal')
    },
    async saveSettings() {
      this.settingsSaving = true
      try {
        const resp = await update_coaching_settings(this.tenant, this.settingsForm)
        this.settings = (resp.value && resp.value.data) || this.settings
        this.$bvModal.hide('cs-settings-modal')
        this.$bvToast.toast('Coaching SLA settings saved.',
          { title: 'Coaching Portal', variant: 'success', autoHideDelay: 3000 })
        // due dates, escalation and the repeat window feed every number on
        // this page — recompute the lot server-side
        this.loadTickets()
        this.loadEffectiveness()
      } catch (e) {
        this.$bvToast.toast(
          this.apiErrorMessage(e, 'Could not save the settings — check the values.'),
          { title: 'Coaching Portal', variant: 'danger', autoHideDelay: 6000 },
        )
      } finally {
        this.settingsSaving = false
      }
    },
  },
}
</script>

<style scoped>
/* the app shell centers text (#app) — this page is left-aligned, so inline
   controls (the mobile Filters toggle) sit left like everything else */
.cs-root { padding-bottom: 24px; text-align: left; }
.cs-stale { opacity: 0.6; }
.cs-tenant-note { margin-top: 10px; font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }
.cs-small { font-size: 0.85em; }
.cs-hint { font-weight: 400; font-size: 0.85em; opacity: 0.7; }
.cs-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cs-num-input { max-width: 90px; }
.cs-positive { color: var(--fi-success, #2f7d32); }

.cs-metrics-row { display: flex; flex-wrap: wrap; gap: 10px; margin: 14px 0 16px; }
.cs-tile {
  flex: 1 1 170px; min-width: 170px; min-height: 96px; padding: 10px 14px;
  background: var(--fi-surface, #f8f9fa);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 6px; text-align: center;
}
.cs-tile h5 { font-size: 0.8em; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.7; margin-bottom: 4px; }
.cs-tile--link { text-decoration: none; color: inherit; }
.cs-tile--link:hover { border-color: var(--fi-ops-accent, #3a6e99); text-decoration: none; color: inherit; }
.cs-tile h2 { margin-bottom: 2px; }

.cs-timeline-hero { margin-bottom: 14px; }

.cs-grid {
  display: grid; gap: 14px;
  /* capped at 3-up (the KPI tile row above keeps its own layout): wider
     screens get roomier widgets instead of a fourth column */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
}
@media (max-width: 1100px) {
  .cs-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 700px) {
  .cs-grid { grid-template-columns: minmax(0, 1fr); }
}
/* uniform widget cards: every small stat panel is the same size, so the
   grid reads as a dashboard rather than a masonry of ragged heights.
   overflow scrolls inside the card; the wide driver tables keep their
   natural height (they paginate instead). */
.cs-panel { display: flex; flex-direction: column; }
.cs-panel:not(.cs-panel--wide) { height: 264px; }
.cs-panel:not(.cs-panel--wide) .cs-panel-body { flex: 1 1 auto; overflow-y: auto; }
.cs-panel--wide { grid-column: 1 / -1; }
.cs-panel-head {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; font-weight: 600; font-size: 0.95em;
  border-bottom: 1px solid var(--fi-line, #dee2e6);
}
.cs-head-tools { display: inline-flex; gap: 6px; margin-left: auto; font-weight: 400; }
.cs-tool { width: 150px; font-size: 0.85em; }
.cs-tool--search { width: 210px; }
.cs-pager {
  display: flex; align-items: center; gap: 10px;
  padding-top: 8px; margin-top: 2px;
  border-top: 1px solid var(--fi-line, #eee);
}
.cs-list-num { font-variant-numeric: tabular-nums; }
/* flag/reason chips: the erm pastel chip family */
.cs-flag-chip {
  display: inline-block; margin: 1px 4px 1px 0;
  padding: 1px 7px;
  font-size: 11px; line-height: 1.5; font-weight: 600;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap;
}
.cs-flag-chip--critical { background: #fbe3e5; color: #b91c1c; border-color: #f5c0c4; }
.cs-flag-chip--warning  { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.cs-flag-chip--positive { background: #ddf3e3; color: #146230; border-color: #b7e2c4; }
/* widget tables: the erm event log table treatment */
.cs-table-inline >>> .table-responsive { overflow-x: auto; overflow-y: hidden; }
.cs-table-inline >>> .table.b-table { font-size: 13px; border-collapse: collapse; }
.cs-table-inline >>> .table.b-table th,
.cs-table-inline >>> .table.b-table td {
  border-left: none !important; border-right: none !important;
  border-bottom: 1px solid #e0e0e0;
  padding: 3px 8px !important;
  vertical-align: middle !important;
  text-align: left !important;
}
.cs-table-inline >>> .table.b-table thead th {
  background: #f8f9fa;
  font-size: 12px; text-transform: uppercase; letter-spacing: 0.3px; color: #555;
  box-shadow: inset 0 -2px 0 0 #ccc;
}
.cs-table-inline >>> .table.b-table tbody tr:hover td { background-color: #f0f5ff; }
.cs-table-inline >>> .cs-driver-cell { white-space: nowrap; width: 1%; }
.cs-table-inline >>> th[aria-sort] { background-image: none !important; white-space: nowrap; }
.cs-table-inline >>> th[aria-sort]::before { display: none !important; }
.cs-table-inline >>> th[aria-sort]::after {
  display: inline-block !important; margin-left: 4px; font-size: 10px; color: #999;
  vertical-align: middle; float: none !important; width: auto !important;
  height: auto !important; opacity: 1 !important;
}
.cs-table-inline >>> th[aria-sort="none"]::after { content: "\2195" !important; }
.cs-table-inline >>> th[aria-sort="ascending"]::after { content: "\25B2" !important; color: var(--fi-rail-accent); }
.cs-table-inline >>> th[aria-sort="descending"]::after { content: "\25BC" !important; color: var(--fi-rail-accent); }
.cs-panel-body { padding: 8px 14px; }
.cs-table-wrap { overflow-x: auto; }

.cs-flag-row {
  display: flex; justify-content: space-between; align-items: center; gap: 8px;
  padding: 4px 0; border-bottom: 1px solid var(--fi-line, #eee);
}
.cs-flag-row:last-child { border-bottom: none; }
.cs-driver-line { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cs-flag-label { margin-right: 8px; }

.cs-bar-row { display: flex; align-items: center; gap: 8px; padding: 2px 0; }
.cs-bar-label {
  flex: 0 0 45%; font-size: 0.85em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cs-bar-track {
  flex: 1; height: 8px; border-radius: 4px;
  background: var(--fi-surface-sunken, #eef1f5); overflow: hidden;
}
.cs-bar-fill { display: block; height: 100%; border-radius: 4px; background: #6f42c1; }
.cs-bar-fill--good { background: var(--fi-success, #2f7d32); }
.cs-bar-fill--warn { background: #d9a84e; }
.cs-bar-fill--risk { background: #dc3545; }
.cs-bar-count { flex: 0 0 44px; text-align: right; font-size: 0.85em; }

.cs-month-row { display: flex; align-items: center; gap: 8px; padding: 2px 0; }
.cs-month-row .cs-bar-label { flex: 0 0 40px; }
.cs-month-bars { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.cs-swatch {
  display: inline-block; width: 10px; height: 10px; border-radius: 3px;
  background: #6f42c1; vertical-align: -1px;
}
.cs-swatch--good { background: var(--fi-success, #2f7d32); }

.cs-label {
  font-size: 0.8em; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.04em; margin-bottom: 5px;
}
.cs-date-input { max-width: 220px; }

/* mobile: the header stays one row — title and actions inline. overrides
   FiPageRail's own 600px rule that wraps actions onto a full-width second
   row (fine for pages with a toolbar; a waste for one info icon). */
@media (max-width: 600px) {
  .cs-rail.fi-pr-rail { flex-wrap: nowrap; row-gap: 0; }
  .cs-rail >>> .fi-pr-actions { width: auto; }
}
</style>
